import { isInBrowser } from '../../common/utils/ssrUtils.js';
import type { Contact, GenericErrorDuplicateContact } from '../../generated/api/models.js';

export interface SubscriptionActionData {
  useDuplicateContact?: GenericErrorDuplicateContact;
  contactCreated?: Contact;
  contactFieldRef?: string;
}

export interface SubscriptionActionStore {
  getSubscriptionAction: () => SubscriptionActionData;
  saveDuplicateContact: (duplicateContact: GenericErrorDuplicateContact) => void;
  saveContactCreated: (contact: Contact, contactFieldRef?: string) => void;
  clear: () => void;
  subscribe: (listener: () => void) => () => void;
}

// Stable, frozen empty state for SSR snapshot
export const EMPTY_SUBSCRIPTION_ACTION: SubscriptionActionData = Object.freeze({});

/**
 * Creates an isolated subscription-action store instance.
 * Use this in SSR to create a fresh store per request and avoid cross-request leakage.
 */
export const createSubscriptionActionStore = (): SubscriptionActionStore => {
  let state: SubscriptionActionData = EMPTY_SUBSCRIPTION_ACTION;
  const listeners = new Set<() => void>();

  const emit = () => listeners.forEach(listener => listener());

  return {
    getSubscriptionAction: () => state,
    saveDuplicateContact: duplicateContact => {
      state = { ...state, useDuplicateContact: duplicateContact, contactCreated: undefined };
      emit();
    },
    saveContactCreated: (contact, contactFieldRef) => {
      state = { ...state, useDuplicateContact: undefined, contactCreated: contact, contactFieldRef };
      emit();
    },
    clear: () => {
      state = EMPTY_SUBSCRIPTION_ACTION;
      emit();
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

// Default instance for client-side use (safe: one user per browser tab).
const defaultStore = createSubscriptionActionStore();

// Non-React accessors for client-side non-React code (epics, dialogs).
export const getSubscriptionAction = defaultStore.getSubscriptionAction;

export const saveDuplicateContact = (duplicateContact: GenericErrorDuplicateContact): void => {
  if (isInBrowser()) {
    defaultStore.saveDuplicateContact(duplicateContact);
  }
};

export const saveContactCreated = (contact: Contact, contactFieldRef?: string): void => {
  if (isInBrowser()) {
    defaultStore.saveContactCreated(contact, contactFieldRef);
  }
};

export const clearSubscriptionAction = (): void => {
  if (isInBrowser()) {
    defaultStore.clear();
  }
};

// Subscription glue for useSyncExternalStore (React face, see SubscriptionActionProvider.tsx)
export const subscribeSubscriptionAction = defaultStore.subscribe;
